import { Business as BusinessIcon } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';
import React from 'react';
import { toast } from 'react-toastify';

import { SWLoadingButton } from '@/components/LoadingButton';
import { getWorstDevice } from '@/components/StatusIcon/statusMapping';
import { useStructures } from '@/Structures/useStructures';
import { DeviceStatus } from '@/types';

import { createStructureRoute } from '../../app-routes';
import AddPropertyButton from '../../components/AddPropertyButton';
import StatusCard from '../../components/StatusCard';
import { Structure } from '../../Structures/types';
import { getUserInfo, updateUserInfo } from '../AccountSettings/api';

// Determines the status of a property displayed on home page Status Card
// based on the status of all devices in the structure
const getStructureStatus = (structure: Structure): DeviceStatus => {
  const allDevices = structure.units.flatMap((unit) => unit.devices);
  if (allDevices.length === 0) return 'NO_DEVICES';

  const worst = getWorstDevice(allDevices);
  return worst?.device_status ?? 'OKAY';
};

const getStructureErrorMessage = (structure: Structure, _status: DeviceStatus): string[] => {
  let errMsg: string[] = [];
  structure.units.forEach((unit) => {
    unit.devices.forEach((device) => {
      errMsg.push(`${unit.name} · ${device.name} · ${device.device_status_strings.join(',')}`);
    });
  });

  return errMsg;
};

const Home = () => {
  const { structures } = useStructures();
  const [orgName, setOrgName] = React.useState<string>();
  const [idUser, setIdUser] = React.useState<string>('');
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    const getData = async () => {
      const userInfo = await getUserInfo();
      setOrgName(userInfo.user.organization_name);
      setIdUser(userInfo.user.id);
      if (userInfo.user.organization_name === '') setOpen(true);
    };
    if (orgName === undefined) getData();
  }, [orgName]);

  const handleSubmit = async () => {
    if (orgName === '')
      toast.warn('The field Organization Name is required', { closeButton: true });
    if (orgName !== undefined && orgName !== '') {
      setLoading(true);
      await updateUserInfo({ organization_name: orgName }, idUser);
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <div data-testid="home-page">
      <Grid container rowSpacing={3} columnSpacing={3}>
        {structures &&
          structures.map((structure: Structure) => {
            const status = getStructureStatus(structure);

            return (
              <Grid item xs="auto" key={structure.id}>
                <StatusCard
                  link={createStructureRoute(structure.id)}
                  title={structure.name}
                  subtitle={`${structure.units.length} Units`}
                  status={status}
                  icon={<BusinessIcon sx={{ width: 60, height: 60 }} />}
                  errorMessage={getStructureErrorMessage(structure, status)}
                />
              </Grid>
            );
          })}
        <Grid item xs="auto">
          <AddPropertyButton />
        </Grid>
      </Grid>
      <Dialog open={open}>
        <DialogTitle>
          <Box fontSize={'42px'} fontWeight={'bold'} textAlign={'center'}>
            Create Organization
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Box textAlign={'center'} fontSize={'18px'} color={'black'}>
              Please provide the name of your organization. This will be shown to tenants when
              sharing water usage.
            </Box>
          </DialogContentText>
          <Box marginTop={3}>
            <TextField
              required
              margin="dense"
              id="orgName"
              name="orgName"
              label="Organization Name"
              fullWidth
              variant="filled"
              // TODO: backend OpenAPI does not enforce this length. Add `maxLength: 64`
              // to every `organization_name` schema in customer-lambdas/doc/webapp.yaml
              // (mirroring users.organization_name varchar(64) in db_setup.sql), then
              // delete this client-side cap + counter. Today the DB write fails with a
              // cryptic string_data_right_truncation error if the cap is bypassed.
              inputProps={{ maxLength: 64 }}
              helperText={`${orgName?.length ?? 0}/64`}
              FormHelperTextProps={{ sx: { textAlign: 'right', m: 0 } }}
              onChange={(evt) => setOrgName(evt.target.value)}
            />
          </Box>
          <Box width={'100%'} marginTop={2}>
            <SWLoadingButton
              variant="contained"
              color="info"
              fullWidth={true}
              onClick={handleSubmit}
              loading={loading}
            >
              Create Organization
            </SWLoadingButton>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;
